<template>
  <div class="container-fluid p-4">
    <div class="box">
      <!-- Header Section: Title and Dropdown -->
      <div class="row mb-3">
        <div class="col-12">
          <div :class="isMobile ? 'd-flex flex-column align-items-start' : 'd-flex justify-content-between align-items-center'">
            <h2 :class="{ 'mb-3': isMobile }">{{ $t('masterEvents.titleList') }}</h2>

            <!-- Master Type Dropdown (top-right on desktop, below title on mobile) -->
            <div class="dropdown" :class="[{ 'mb-3': isMobile }, { show: isDropdownOpen }]">
              <button
                class="btn btn-outline-secondary dropdown-toggle"
                type="button"
                :aria-expanded="isDropdownOpen"
                @click="toggleDropdown">
                {{ $t('masterEvents.title') }}
              </button>
              <ul class="dropdown-menu" :class="{ show: isDropdownOpen }">
                <li>
                  <a class="dropdown-item"
                    href="/master/event_organizers"
                    @click.prevent="changeType('event_organizer')">
                    {{ $t('eventOrganizers.title') }}
                  </a>
                </li>
                <li>
                  <a class="dropdown-item active"
                    href="/master/events"
                    @click.prevent="changeType('event')">
                    {{ $t('masterEvents.title') }}
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <!-- Search Box and New Button -->
      <div class="row mb-3">
        <div :class="isMobile ? 'col-12 mb-3' : 'col-md-6'">
          <div class="search-box">
            <input
              type="text"
              class="form-control search-input"
              v-model="filter.title"
              :placeholder="$t('masterEvents.searchPlaceholder')"
              @input="handleSearchInput"
            />
            <i class="bi bi-search search-icon"></i>
          </div>
        </div>
        <div :class="isMobile ? 'col-12' : 'col-md-6 text-end'">
          <button class="btn btn-primary" @click="createNew">
            <i class="bi bi-plus-circle"></i> {{ $t('masterEvents.addNew') }}
          </button>
        </div>
      </div>

      <!-- Table with horizontal scroll -->
      <div class="table-wrapper">
        <div v-if="page" class="table-scroll">
          <table class="table table-hover">
            <thead>
              <tr>
                <th>{{ $t('masterEvents.eventName') }}</th>
                <th>{{ $t('masterEvents.startDate') }}</th>
                <th>{{ $t('masterEvents.endDate') }}</th>
                <th>{{ $t('masterEvents.capacity') }}</th>
                <th>{{ $t('masterEvents.forecastedVisitorCount') }}</th>
                <th>{{ $t('masterEvents.isOnline') }}</th>
                <th>{{ $t('masterEvents.organizer') }}</th>
                <th>{{ $t('masterEvents.feeMax') }}</th>
                <th>{{ $t('masterEvents.feeMin') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="event in page.items"
                :key="event.id"
                @click="editEvent(event.id)"
                class="cursor-pointer"
              >
                <td>{{ event.title }}</td>
                <td>{{ event.startDate || '-' }}</td>
                <td>{{ event.endDate || '-' }}</td>
                <td>{{ event.capacity || '-' }}</td>
                <td>{{ event.forecastedVisitorCount || '-' }}</td>
                <td>{{ event.isOnline ? '◯' : '-' }}</td>
                <td>{{ event.primaryOrganizerName || '-' }}</td>
                <td class="text-end">{{ formatFee(event.maxFee) }}</td>
                <td class="text-end">{{ formatFee(event.minFee) }}</td>
              </tr>
              <tr v-if="page.items.length === 0">
                <td colspan="8" class="text-center text-muted py-4">
                  {{ $t('masterEvents.noData') }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="text-center py-4">
          <div class="spinner-border" role="status">
            <span class="visually-hidden">{{ $t('masterEvents.loading') }}</span>
          </div>
        </div>

        <!-- Pagination -->
        <SimplePagination
          v-if="page"
          :page="page"
          @change="changePage"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import EventFilter from '../../../../filters/user/EventFilter';
import usePagination from '../../../../composables/usePagination';
import SimplePagination from '../../../../components/SimplePagination.vue';
import { useIsMobile } from '../../../../utils/DeviceUtil';
import { colors } from '../../../../../consts/colors';

const router = useRouter();
const filter = ref(new EventFilter({}, { isMaster: true }));
const { isMobile } = useIsMobile();

const { page, search, changePage } = usePagination(filter.value);

// Debounce timer for search
const searchTimer = ref(null);

// Dropdown state
const isDropdownOpen = ref(false);

/**
 * Toggle dropdown menu
 */
const toggleDropdown = () => {
  isDropdownOpen.value = !isDropdownOpen.value;
};

/**
 * Close dropdown when clicking outside
 */
const handleClickOutside = (event) => {
  const dropdown = document.querySelector('.dropdown');
  if (dropdown && !dropdown.contains(event.target)) {
    isDropdownOpen.value = false;
  }
};

// Add/remove event listener for clicking outside
onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});

/**
 * Change master data type
 * @param {string} type - Master data type ('event_organizer', 'event', etc.)
 */
const changeType = (type) => {
  // Close dropdown
  isDropdownOpen.value = false;

  // Navigate to different master data pages based on type
  switch (type) {
    case 'event_organizer':
      router.push({ name: 'master.event_organizers.index' });
      break;
    case 'event':
      // Already on this page
      break;
    default:
      console.warn(`Unknown master data type: ${type}`);
      break;
  }
};

/**
 * Handle search input with debounce
 */
const handleSearchInput = () => {
  // Clear existing timer
  if (searchTimer.value) {
    clearTimeout(searchTimer.value);
  }

  // Set new timer to execute search after 200ms
  searchTimer.value = setTimeout(() => {
    search();
  }, 200);
};

/**
 * Format fee for display
 * @param {number|null} fee
 * @returns {string}
 */
const formatFee = (fee) => {
  if (fee === null || fee === undefined) {
    return '-';
  }
  return `¥${fee.toLocaleString()}`;
};

const createNew = () => {
  router.push({ name: 'master.events.new' });
};

const editEvent = (id) => {
  router.push({ name: 'master.events.edit', params: { id } });
};
</script>

<style lang="scss" scoped>
.container-fluid {
  max-width: 1400px;
}

h2 {
  font-size: 1.5rem;
  font-weight: 600;
  color: v-bind('colors.textPrimary');
}

.box {
  background-color: v-bind('colors.white');
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 1px 3px v-bind('colors.shadowColor');
}

// Search box with icon
.search-box {
  position: relative;
  width: 100%;
}

.search-input {
  width: 100%;
  padding-right: 40px;
  border-color: v-bind('colors.border');

  &:focus {
    border-color: v-bind('colors.blue[500]');
    box-shadow: 0 0 0 0.2rem v-bind('colors.shadowPrimary');
  }
}

.search-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: v-bind('colors.gray[500]');
  font-size: 16px;
  pointer-events: none;
}

// Buttons
.btn-primary {
  background-color: v-bind('colors.blue[500]');
  border-color: v-bind('colors.blue[500]');
  padding: 0.5rem 1.5rem;
  font-size: 0.95rem;
  font-weight: 500;

  &:hover:not(:disabled) {
    background-color: v-bind('colors.blue[600]');
    border-color: v-bind('colors.blue[600]');
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

// Dropdown button
.dropdown {
  .btn-outline-secondary {
    color: v-bind('colors.gray[600]');
    border-color: v-bind('colors.border');
    padding: 0.5rem 1rem;
    font-size: 0.95rem;

    &:hover {
      background-color: v-bind('colors.gray[50]');
      border-color: v-bind('colors.gray[400]');
      color: v-bind('colors.gray[700]');
    }
  }

  .dropdown-menu {
    border-color: v-bind('colors.border');
    box-shadow: 0 2px 8px v-bind('colors.shadowColor');

    .dropdown-item {
      color: v-bind('colors.textPrimary');

      &:hover {
        background-color: v-bind('colors.gray[50]');
      }

      &.active {
        background-color: v-bind('colors.blue[500]');
        color: v-bind('colors.white');
      }
    }
  }
}

// Table styles
.table {
  margin-bottom: 0;
  font-size: 0.95rem;

  thead th {
    border-bottom: 2px solid v-bind('colors.gray[200]');
    font-weight: 600;
    color: v-bind('colors.textPrimary');
    background-color: v-bind('colors.gray[50]');
    padding: 0.75rem;
    white-space: nowrap;

    // Column widths
    &:nth-child(1) { min-width: 300px; } // イベント名
    &:nth-child(2) { min-width: 120px; } // 開始日
    &:nth-child(3) { min-width: 120px; } // 終了日
    &:nth-child(4) { min-width: 80px; }  // 定員
    &:nth-child(5) { min-width: 100px; } // オンライン
    &:nth-child(6) { min-width: 200px; } // 主催者
    &:nth-child(7) { min-width: 120px; } // 費用（上限）
    &:nth-child(8) { min-width: 120px; } // 費用（下限）
  }

  tbody {
    td {
      padding: 0.75rem;
      color: v-bind('colors.textSecondary');
      vertical-align: middle;
    }

    .cursor-pointer {
      cursor: pointer;
      transition: background-color 0.15s ease;

      &:hover {
        background-color: v-bind('colors.bgHover');
      }
    }
  }

  .text-muted {
    color: v-bind('colors.gray[500]') !important;
  }
}

// Loading spinner
.spinner-border {
  color: v-bind('colors.blue[500]');
}

// Table wrapper with horizontal scroll and pagination
.table-wrapper {
  position: relative;
}

.table-scroll {
  overflow-x: auto;
  overflow-y: visible;
  width: 100%;

  // Custom scrollbar styling
  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: v-bind('colors.gray[100]');
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: v-bind('colors.gray[400]');
    border-radius: 4px;

    &:hover {
      background: v-bind('colors.gray[500]');
    }
  }
}

// Ensure table doesn't collapse
.table-scroll table {
  min-width: 1200px;
  margin-bottom: 0;
}
</style>
